import {Command} from '@application/command/command';
import {CalendarItemGroup} from '@domain/planning/calendar-item-group';
import {PlanningItem} from '@domain/planning/planning-item';
import {PlanningMachineRun} from '@domain/planning/planning-machine-run';
import {PlanningOrder} from '@domain/planning/planning-order';
import {moment, TimeUtils} from '@vdw/angular-component-library';
import {each, findIndex, isEmpty, isEqual, isNil, remove} from 'lodash-es';
import {RescheduleOverlappingItemsCommand} from '../reschedule-overlapping-items/reschedule-overlapping-items-command';
import {ReschedulePlanningOrderParameters} from './reschedule-planning-order-parameters';

export class ReschedulePlanningOrderCommand implements Command<ReschedulePlanningOrderParameters, CalendarItemGroup[]> {
  public execute(commandArgs: ReschedulePlanningOrderParameters): CalendarItemGroup[] {
    const {draggedPlanningOrder, sourceRun, destinationRun, groupedCalendarItems} = commandArgs;

    let {planningOrderNewPosition} = commandArgs;

    const isSourceRunDifferentFromDestinationRun = !isEqual(sourceRun.id, destinationRun.id);

    if (!isSourceRunDifferentFromDestinationRun) {
      const planningOrderInitialPosition = findIndex(destinationRun.planningOrders, {id: draggedPlanningOrder.id});
      planningOrderNewPosition = planningOrderInitialPosition < planningOrderNewPosition ? planningOrderNewPosition - 1 : planningOrderNewPosition;
    }

    const removedPlanningOrder = this.removePlanningOrder(sourceRun, draggedPlanningOrder);

    this.addPlanningOrder(destinationRun, draggedPlanningOrder, planningOrderNewPosition);
    this.rescheduleRuns(isSourceRunDifferentFromDestinationRun, sourceRun, destinationRun, removedPlanningOrder.totalRunningTimeInMs);

    const destinationGroupedCalendarItem: CalendarItemGroup = this.getDestinationGroupedCalendarItemAndUpdateGroupedCalendarItems(
      sourceRun,
      destinationRun,
      groupedCalendarItems,
      isSourceRunDifferentFromDestinationRun
    );

    new RescheduleOverlappingItemsCommand().execute({planningItem: destinationRun, groupedCalendarItem: destinationGroupedCalendarItem});

    return groupedCalendarItems;
  }

  private removePlanningOrder(run: PlanningMachineRun, draggedPlanningOrder: PlanningOrder): PlanningOrder {
    const [removedPlanningOrder] = remove(run.planningOrders, {id: draggedPlanningOrder.id});
    return removedPlanningOrder;
  }

  private addPlanningOrder(run: PlanningMachineRun, draggedPlanningOrder: PlanningOrder, planningOrderNewPosition: number): void {
    run.planningOrders.splice(planningOrderNewPosition, 0, draggedPlanningOrder);
  }

  private rescheduleRuns(isSourceRunDifferentFromDestinationRun: boolean, sourceRun: PlanningMachineRun, destinationRun: PlanningMachineRun, planningOrderTotalRunTimeInMs: number): void {
    if (isSourceRunDifferentFromDestinationRun) {
      if (!isEmpty(sourceRun.planningOrders)) {
        sourceRun.reschedule(sourceRun.startDate, moment(sourceRun.endDate).subtract(planningOrderTotalRunTimeInMs, TimeUtils.MILLISECONDS_UNIT).toDate());
      } else {
        sourceRun.reschedule(sourceRun.startDate, sourceRun.endDate);
      }

      destinationRun.rescheduleBasedOnPlanningOrders();
    } else {
      destinationRun.reschedule(destinationRun.startDate, destinationRun.endDate);
    }
  }

  private getDestinationGroupedCalendarItemAndUpdateGroupedCalendarItems(
    sourceRun: PlanningMachineRun,
    destinationRun: PlanningMachineRun,
    groupedCalendarItems: CalendarItemGroup[],
    sourceRunIsDifferentFromDestinationRun: boolean
  ): CalendarItemGroup {
    let sourceGroupedCalendarItem: CalendarItemGroup;
    let destinationGroupedCalendarItem: CalendarItemGroup;
    let alreadyUpdatedSourceAndDestinationCalendarItem = false;

    each(groupedCalendarItems, (groupedCalendarItem: CalendarItemGroup) => {
      each(groupedCalendarItem.items, (item: PlanningItem, index: number) => {
        if (!sourceRunIsDifferentFromDestinationRun && isEqual(item.uniqueId, destinationRun.uniqueId)) {
          groupedCalendarItem.items[index] = destinationRun;
          sourceGroupedCalendarItem = groupedCalendarItem;
          destinationGroupedCalendarItem = groupedCalendarItem;
        } else {
          if (isEqual(item.uniqueId, sourceRun.uniqueId)) {
            groupedCalendarItem.items[index] = sourceRun;
            sourceGroupedCalendarItem = groupedCalendarItem;
          } else if (isEqual(item.uniqueId, destinationRun.uniqueId)) {
            groupedCalendarItem.items[index] = destinationRun;
            destinationGroupedCalendarItem = groupedCalendarItem;
          }
        }

        alreadyUpdatedSourceAndDestinationCalendarItem = !isNil(sourceGroupedCalendarItem) && !isNil(destinationGroupedCalendarItem);

        return !alreadyUpdatedSourceAndDestinationCalendarItem;
      });

      return !alreadyUpdatedSourceAndDestinationCalendarItem;
    });

    return destinationGroupedCalendarItem;
  }
}
